import { useState } from "react";
import axios from "axios";
import { X, ChevronLeft, ChevronRight, Check, Play, Tag, FormInput, Sparkles, Code2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import MediaUrlAdder from "@/components/dashboard/MediaUrlAdder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const authHeaders = () => {
  const t = localStorage.getItem("auth_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const TYPES = [
  { id: "watch_now", title: "Watch Now on Listing", desc: "Visitor fills the form and watches immediately. Best for evergreen content." },
  { id: "scheduled", title: "Scheduled Evergreen", desc: "Visitor picks a date/time. Reminders + commitment. (Coming next phase — saves as scheduled for now.)" },
  { id: "replay", title: "Replay / On-Demand", desc: "Use for missed-session follow-up or low-friction watching." },
];

export default function LivecastWizard({ businesses, onClose, onCreated, initial }) {
  const isEdit = Boolean(initial?.id);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => {
    if (isEdit) {
      return {
        business_id: initial.business_id || businesses?.[0]?.id || "",
        type: initial.type || "watch_now",
        title: initial.title || "",
        short_description: initial.short_description || "",
        long_description: initial.long_description || "",
        host_name: initial.host_name || "",
        host_title: initial.host_title || "",
        video: initial.video || { url: "", provider: "", video_id: "", embed_url: "", thumbnail: "", title: "" },
        offer: initial.offer || { headline: "", description: "", button_text: "Claim Offer", button_url: "" },
        lead_form: initial.lead_form || { show_business_category: false, show_question: true, consent_required: true },
        enable_ai_assistant: initial.enable_ai_assistant !== false,
        listing_placement: initial.listing_placement || "below_hero",
        publish: initial.published !== false,
        custom_embed_code: initial.custom_embed_code || "",
        custom_embed_placement: initial.custom_embed_placement || "below_form",
        custom_embed_label: initial.custom_embed_label || "",
      };
    }
    return {
      business_id: businesses?.[0]?.id || "",
      type: "watch_now",
      title: "",
      short_description: "",
      long_description: "",
      host_name: "",
      host_title: "",
      video: { url: "", provider: "", video_id: "", embed_url: "", thumbnail: "", title: "" },
      offer: { headline: "", description: "", button_text: "Claim Offer", button_url: "" },
      lead_form: { show_business_category: false, show_question: true, consent_required: true },
      enable_ai_assistant: true,
      listing_placement: "below_hero",
      publish: true,
      custom_embed_code: "",
      custom_embed_placement: "below_form",
      custom_embed_label: "",
    };
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const updateNested = (k, sub, v) => setForm((p) => ({ ...p, [k]: { ...p[k], [sub]: v } }));

  const stepCount = 5;

  const canNext = (() => {
    if (step === 1) return !!form.business_id && !!form.type;
    if (step === 2) return form.title.trim().length > 0;
    if (step === 3) return !!form.video.embed_url || !!form.video.url;
    return true;
  })();

  const submit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        // PATCH — strip business_id (not editable after creation)
        const { business_id: _bid, ...patch } = form;
        const res = await axios.patch(`${API}/livecasts/${initial.id}`, patch, { headers: authHeaders() });
        toast.success(`LiveCast "${res.data.title}" updated!`);
      } else {
        const res = await axios.post(`${API}/livecasts`, form, { headers: authHeaders() });
        toast.success(`LiveCast "${res.data.title}" created!`);
      }
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isEdit ? "Update failed" : "Create failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6" data-testid="livecast-wizard">
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-[#0A0F1C] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#F4B41A] font-semibold">Step {step} of {stepCount}</p>
            <h2 className="text-lg font-bold text-white">{isEdit ? "Edit LiveCast™" : "Create LiveCast™"}</h2>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-white" data-testid="wizard-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Progress dots */}
        <div className="flex gap-1.5 px-5 pt-3">
          {Array.from({ length: stepCount }).map((_, i) => (
            <span key={i} className={`flex-1 h-1 rounded-full ${i + 1 <= step ? "bg-[#F4B41A]" : "bg-white/10"}`} />
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {step === 1 && <StepType form={form} update={update} businesses={businesses} />}
          {step === 2 && <StepDetails form={form} update={update} updateNested={updateNested} />}
          {step === 3 && <StepMedia form={form} updateNested={updateNested} />}
          {step === 4 && <StepLeadAndOffer form={form} update={update} updateNested={updateNested} />}
          {step === 5 && <StepCustomEmbed form={form} update={update} />}
        </div>

        <footer className="flex items-center justify-between px-5 py-4 border-t border-white/5">
          <Button
            variant="outline"
            disabled={step === 1}
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            className="border-white/15 bg-white/5 hover:bg-white/10 text-white"
            data-testid="wizard-prev"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {step < stepCount ? (
            <Button
              disabled={!canNext}
              onClick={() => setStep((s) => Math.min(stepCount, s + 1))}
              className="bg-[#F4B41A] hover:bg-[#E0A015] text-[#0A0F1C] font-semibold"
              data-testid="wizard-next"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              disabled={saving}
              onClick={submit}
              className="bg-gradient-to-r from-[#F4B41A] to-[#E0A015] hover:opacity-90 text-[#0A0F1C] font-bold"
              data-testid="wizard-publish"
            >
              {saving ? (isEdit ? "Saving…" : "Publishing…") : (
                <><Check className="w-4 h-4 mr-1.5" /> {isEdit ? "Save Changes" : "Publish to Listing"}</>
              )}
            </Button>
          )}
        </footer>
      </div>
    </div>
  );
}

/* ---------------- Steps ---------------- */

function StepType({ form, update, businesses }) {
  return (
    <>
      <SectionTitle icon={Tag} title="Type & Listing" subtitle="Pick the LiveCast format and the business it belongs to." />
      <div>
        <label className="block text-sm text-[#94A3B8] mb-1.5">Connected Listing</label>
        <select
          value={form.business_id}
          onChange={(e) => update("business_id", e.target.value)}
          className="w-full h-10 px-3 rounded-md bg-[#0F172A] border border-white/15 text-white text-sm"
          style={{ colorScheme: "dark" }}
          data-testid="wizard-business-select"
        >
          {(businesses || []).map((b) => (
            <option key={b.id} value={b.id} className="bg-[#0F172A] text-white">{b.name}</option>
          ))}
        </select>
      </div>
      <div className="space-y-2.5 mt-4">
        {TYPES.map((t) => (
          <label
            key={t.id}
            className={`block rounded-xl border p-4 cursor-pointer transition-colors ${
              form.type === t.id ? "border-[#F4B41A] bg-[#F4B41A]/[0.07]" : "border-white/10 hover:border-white/25"
            }`}
            data-testid={`wizard-type-${t.id}`}
          >
            <div className="flex items-start gap-3">
              <input type="radio" name="type" value={t.id} checked={form.type === t.id} onChange={(e) => update("type", e.target.value)} className="mt-1 accent-[#F4B41A]" />
              <div>
                <p className="text-white font-semibold">{t.title}</p>
                <p className="text-sm text-[#94A3B8] mt-0.5">{t.desc}</p>
              </div>
            </div>
          </label>
        ))}
      </div>
    </>
  );
}

function StepDetails({ form, update }) {
  return (
    <>
      <SectionTitle icon={Sparkles} title="Main Details" subtitle="Title, description, and host info shown on the listing preview." />
      <Field label="LiveCast Title *">
        <Input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="e.g. AI Lead Engine Masterclass" className="bg-[#0F172A] border-white/10 text-white" data-testid="wizard-title" />
      </Field>
      <Field label="Short Description">
        <Input value={form.short_description} onChange={(e) => update("short_description", e.target.value)} placeholder="One-line hook (shown on the listing card)" className="bg-[#0F172A] border-white/10 text-white" />
      </Field>
      <Field label="Long Description">
        <Textarea value={form.long_description} onChange={(e) => update("long_description", e.target.value)} placeholder="What viewers will learn / why they should watch." rows={4} className="bg-[#0F172A] border-white/10 text-white" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Host / Presenter Name">
          <Input value={form.host_name} onChange={(e) => update("host_name", e.target.value)} className="bg-[#0F172A] border-white/10 text-white" />
        </Field>
        <Field label="Host Title">
          <Input value={form.host_title} onChange={(e) => update("host_title", e.target.value)} placeholder="e.g. CEO, Founder, Loan Specialist" className="bg-[#0F172A] border-white/10 text-white" />
        </Field>
      </div>
    </>
  );
}

function StepMedia({ form, updateNested }) {
  const v = form.video || {};
  const hasVideo = !!(v.embed_url || v.url);
  return (
    <>
      <SectionTitle icon={Play} title="Video / Replay Source URL or Embed Code" subtitle="Paste a YouTube, Facebook, Vimeo, StreamYard, Zoom recording, Twitch, Kick, LinkedIn, Rumble, TikTok, Instagram, Amazon Live, Whatnot, custom video URL, or iframe embed code." />
      {!hasVideo ? (
        <div className="rounded-xl border border-dashed border-white/15 p-5 bg-white/[0.02]">
          <MediaUrlAdder
            acceptType="video"
            buttonLabel="Add Video Source"
            onAdd={(item) => {
              Object.entries(item).forEach(([k, val]) => updateNested("video", k, val));
            }}
          />
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-black overflow-hidden">
          {v.embed_url ? (
            <div className="aspect-video">
              <iframe src={v.embed_url} title={v.title || form.title} allowFullScreen className="w-full h-full" style={{ border: 0 }} />
            </div>
          ) : (
            <video src={v.url} controls className="w-full aspect-video" />
          )}
          <div className="flex items-center justify-between px-3 py-2 bg-white/[0.04]">
            <span className="text-xs text-[#94A3B8] capitalize">{v.provider?.replace("_", " ")} {v.title ? `· ${v.title}` : ""}</span>
            <button onClick={() => { ["url","provider","video_id","embed_url","thumbnail","title"].forEach(k => updateNested("video", k, "")); }} className="text-xs text-red-300 hover:text-red-200">
              Replace
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function StepLeadAndOffer({ form, update, updateNested }) {
  return (
    <>
      <SectionTitle icon={FormInput} title="Lead Form & Offer" subtitle="What you'll capture, and the CTA shown while they watch." />

      <div className="rounded-xl border border-white/10 p-4 space-y-3 bg-white/[0.02]">
        <p className="text-sm font-semibold text-white">Lead Form</p>
        <p className="text-xs text-[#94A3B8]">Required: First name, Last name, Email, Phone, City, State.</p>
        <label className="flex items-center gap-2 text-sm text-white">
          <input type="checkbox" checked={form.lead_form.show_question} onChange={(e) => updateNested("lead_form", "show_question", e.target.checked)} className="accent-[#F4B41A]" />
          Show optional &ldquo;Question&rdquo; field
        </label>
        <label className="flex items-center gap-2 text-sm text-white">
          <input type="checkbox" checked={form.lead_form.show_business_category} onChange={(e) => updateNested("lead_form", "show_business_category", e.target.checked)} className="accent-[#F4B41A]" />
          Show &ldquo;Business Category&rdquo; (business-facing events only)
        </label>
        <label className="flex items-center gap-2 text-sm text-white">
          <input type="checkbox" checked={form.enable_ai_assistant} onChange={(e) => update("enable_ai_assistant", e.target.checked)} className="accent-[#F4B41A]" />
          Enable AI Event Assistant chat on watch page
        </label>
      </div>

      <div className="rounded-xl border border-white/10 p-4 space-y-3 bg-white/[0.02]">
        <p className="text-sm font-semibold text-white">Offer / CTA (shown next to the video)</p>
        <Field label="Offer Headline">
          <Input value={form.offer.headline} onChange={(e) => updateNested("offer", "headline", e.target.value)} placeholder="e.g. Free 30-min Strategy Session" className="bg-[#0F172A] border-white/10 text-white" />
        </Field>
        <Field label="Offer Description">
          <Input value={form.offer.description} onChange={(e) => updateNested("offer", "description", e.target.value)} placeholder="One-line value prop" className="bg-[#0F172A] border-white/10 text-white" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Button Text">
            <Input value={form.offer.button_text} onChange={(e) => updateNested("offer", "button_text", e.target.value)} className="bg-[#0F172A] border-white/10 text-white" />
          </Field>
          <Field label="Button URL">
            <Input value={form.offer.button_url} onChange={(e) => updateNested("offer", "button_url", e.target.value)} placeholder="https://" className="bg-[#0F172A] border-white/10 text-white" />
          </Field>
        </div>
      </div>
    </>
  );
}


function StepCustomEmbed({ form, update }) {
  const PLACEMENTS = [
    { id: "above_form", label: "Above the registration form" },
    { id: "below_form", label: "Below the registration form" },
    { id: "after_watch", label: "After the visitor clicks Watch Now" },
  ];

  return (
    <>
      <SectionTitle
        icon={Code2}
        title="Custom Embed Code (optional)"
        subtitle="Paste any 3rd-party widget HTML (calendar, scheduler, quiz, chat, payment, RSVP). It renders right on the LiveCast sign-up page next to the Watch Now button — `<script>` tags are supported."
      />

      <div className="rounded-xl border border-white/10 p-4 space-y-3 bg-white/[0.02]">
        <Field label="Where should it appear?">
          <select
            value={form.custom_embed_placement || "below_form"}
            onChange={(e) => update("custom_embed_placement", e.target.value)}
            className="w-full h-10 px-3 rounded-md bg-[#0F172A] border border-white/15 text-white text-sm"
            style={{ colorScheme: "dark" }}
            data-testid="wizard-embed-placement"
          >
            {PLACEMENTS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Section Label (optional)">
          <Input
            value={form.custom_embed_label || ""}
            onChange={(e) => update("custom_embed_label", e.target.value)}
            placeholder='e.g. "Book Your Strategy Call" or "Apply to the Pilot Program"'
            className="bg-[#0F172A] border-white/10 text-white"
            data-testid="wizard-embed-label"
          />
        </Field>

        <Field label="Paste your embed HTML / script">
          <Textarea
            value={form.custom_embed_code || ""}
            onChange={(e) => update("custom_embed_code", e.target.value)}
            rows={8}
            placeholder={'<!-- e.g. Calendly, KnockKnock, Typeform, JotForm, Lead Connector -->\n<div class="my-widget">…</div>\n<script src="https://example.com/widget.js"></script>'}
            className="bg-[#0F172A] border-white/10 text-white font-mono text-xs"
            data-testid="wizard-embed-code"
          />
        </Field>

        <p className="text-[11px] text-[#64748B] leading-relaxed">
          ✓ &lt;script&gt; tags are supported and executed safely via our EmbedRenderer.
          {" "}✓ The block is wrapped automatically and stays mobile-friendly.
          {" "}✓ Leave blank to skip — your LiveCast still publishes normally.
        </p>
      </div>
    </>
  );
}


function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-9 h-9 rounded-lg bg-[#F4B41A]/15 border border-[#F4B41A]/30 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#F4B41A]" />
      </span>
      <div>
        <h3 className="text-white font-semibold">{title}</h3>
        <p className="text-sm text-[#94A3B8] leading-relaxed">{subtitle}</p>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm text-[#94A3B8] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
